import Game from "../../../core/engine/game";
import { createLocalGame } from "../../game/game-factory";
import { IInputSource } from "../../managers/input-source";
import { GameController } from "../../controller/GameController";
import { MultiplayerGameAdapter } from "../adapter/MultiplayerGameAdapter";
import { Packet, EndgamePacket } from "../../../core/protocol/packets";
import { PacketTypes } from "../../../core/protocol/types";

/**
 * Manages local Game instance for the current player
 * Provides symmetry with RemoteGameManager
 */
export default class LocalGameManager {
  private localGame: Game | null = null;
  private unifiedInput: any = null;
  private controller: GameController | null = null;
  private initialGameSeed: number | undefined = undefined;
  private gameInitialized: boolean = false;
  private queuePacketRegistrar?: (callback: (packet: Packet) => void) => void;

  setQueuePacketRegistrar(
    registrar: (callback: (packet: Packet) => void) => void
  ): void {
    this.queuePacketRegistrar = registrar;
  }

  /**
   * Create or update local game
   * @param canvas Canvas element
   * @param gameSeed Game seed for deterministic pieces
   * @param controller Game controller for multiplayer coordination
   * @param inputSources Input sources (keyboard, touch, etc.)
   */
  createOrUpdateLocalGame(
    canvas: HTMLCanvasElement,
    gameSeed: number | undefined,
    controller: GameController,
    inputSources: IInputSource[]
  ): Game {
    // If game already exists, return it (update case)
    if (this.localGame) {
      return this.localGame;
    }

    // CRITICAL: Capture the seed only on initial game creation
    // This prevents the game from being recreated with a new seed when P_SERVERRANDOM arrives
    const seedToUse = this.initialGameSeed ?? gameSeed;
    if (this.initialGameSeed === undefined && gameSeed !== undefined) {
      this.initialGameSeed = gameSeed;
      console.log(
        `[LocalGameManager] 🎲 Captured initial game seed: ${this.initialGameSeed}`
      );
    }

    console.log(
      `[LocalGameManager] Creating local game with seed: ${seedToUse} (gameSeed=${gameSeed})`
    );
    const { game, unifiedInput } = createLocalGame(
      canvas,
      undefined,
      undefined,
      inputSources,
      seedToUse
    );

    this.localGame = game;
    this.unifiedInput = unifiedInput;
    this.controller = controller;

    game.isMultiplayer = true;

    // Set numPlayer from controller state
    const gameState = controller.getGameState();
    const localPlayer = gameState?.connections.get(gameState.localConnectionId);
    if (localPlayer) {
      game.canvas.numPlayer = localPlayer.numPlayer;
      console.log(
        `[LocalGameManager] Set local canvas.numPlayer = ${game.canvas.numPlayer}`
      );
    }

    // CRITICAL: Local player's Canvas should NOT wait for P_DOWNLOAD
    game.canvas.waitDownload = false;
    console.log(`[LocalGameManager] Set local canvas.waitDownload = false`);

    // Start the local game immediately
    game.start();
    console.log("[LocalGameManager] Started local game");

    // Setup adapter
    const adapter = new MultiplayerGameAdapter(
      () => this.controller!,
      () => game.canvas
    );
    game.setMultiplayerAdapter(adapter);

    // Setup event handlers
    game.onLinesCleared = (
      lines: number,
      complexity: number,
      isClean: boolean,
      holePositions: number[]
    ) => {
      if (this.controller) {
        const attack = {
          lines: lines,
          complexity: complexity,
          type: isClean ? "clean" : ("normal" as "normal" | "clean" | "potato"),
          sourceConnectionId: this.controller.getPeerId(),
          holePositions: holePositions,
        };
        adapter.sendAttack?.(attack);
      }
    };

    // Setup packet queue callback
    const queuePacketCallback = (packet: Packet) => {
      if (game && game.stackPacket) {
        game.stackPacket(packet);
      }
    };
    if (this.queuePacketRegistrar) {
      this.queuePacketRegistrar(queuePacketCallback);
    } else if (this.controller) {
      this.controller.setQueuePacketCallback(queuePacketCallback);
    }

    // Setup pending attacks handler
    const applyPendingAttacks = () => {
      if (!this.controller || !this.localGame) return;

      const packets = this.controller.getPendingPackets();
      const currentState = this.controller.getGameState();

      for (const packet of packets) {
        const attack = packet.attack;
        const attacker = currentState?.players?.get(attack.sourceConnectionId);
        const attackerColor = attacker?.team || 7;
        const holePositions = attack.holePositions || [];

        const senderNumPlayer =
          (packet as any).sender !== undefined && (packet as any).sender !== 255
            ? (packet as any).sender
            : undefined;

        this.localGame.canvas.addPacket(
          attackerColor,
          attack.lines,
          attack.complexity,
          attack.type,
          holePositions,
          senderNumPlayer,
          attack.param
        );
      }
    };

    game.processPendingAttacks = applyPendingAttacks;

    this.gameInitialized = true;
    return game;
  }

  /**
   * Get the local game instance
   */
  getLocalGame(): Game | null {
    return this.localGame;
  }

  /**
   * Get the unified input manager
   */
  getUnifiedInput(): any {
    return this.unifiedInput;
  }

  /**
   * Step the local game
   */
  step(): void {
    if (this.localGame && this.localGame.player?.isLocal()) {
      this.localGame.step();
    }
  }

  /**
   * Process P_SERVERRANDOM packet
   */
  processServerRandomPacket(): void {
    if (!this.localGame) return;

    const serverRandomPacket = this.localGame.peekPacket(
      PacketTypes.SERVER_RANDOM
    );
    if (serverRandomPacket) {
      const randomPacket = serverRandomPacket as any;
      const oldSeed = this.localGame.canvas.rnd.get_seed();
      console.log(
        `[LocalGameManager] 🔄 Processing P_SERVERRANDOM: changing seed ${oldSeed} → ${randomPacket.seed}`
      );

      this.localGame.canvas.setSeed(randomPacket.seed);
      this.localGame.removePacket();
    }
  }

  /**
   * Process P_ENDGAME packet (matches original check_end_game() client side)
   * Reference: quadra/source/net_list.cc:746-761
   */
  processEndgamePacket(): void {
    // Match original: check_end_game() calls game->peekpacket(P_ENDGAME) every frame
    // Reference: quadra/source/net_list.cc:746-761
    // Original peekpacket() only checks front of queue, so P_ENDGAME must be at front to be processed
    // The original processes P_LINES in a while loop first (lines 405-410), clearing the way
    // Then it checks P_ENDGAME (line 746)
    if (!this.localGame || !this.controller) {
      return;
    }

    // Match original: process P_LINES in a while loop first (net_list.cc:405-410)
    // This clears P_LINES packets from the queue, allowing P_ENDGAME to reach the front
    // Reference: while((p = (Packet_lines *)game->peekpacket(P_LINES))) { sendlines(p); game->removepacket(); }
    while (
      this.localGame.peekPacket(PacketTypes.PLAYER_LINES)
    ) {
      // P_LINES should have been processed by the state machine, but remove from queue to match original
      // Original: sendlines() processes the packet, then removepacket() removes it
      this.localGame.removePacket();
    }

    // Match original: check P_ENDGAME at front of queue (net_list.cc:746)
    // Reference: Packet_endgame *p=(Packet_endgame *) game->peekpacket(P_ENDGAME);
    const packet = this.localGame.peekPacket(PacketTypes.ENDGAME);
    const endgamePacket = packet as EndgamePacket | null;
    if (endgamePacket) {
      const reason = endgamePacket.autoEnd ? "auto" : "manual";
      console.log(
        `[LocalGameManager.processEndgamePacket] Processing P_ENDGAME: reason=${reason}, setting status to finished`
      );

      // Match original: game->endgame() sets terminated=true (game.cc:808-809)
      // Reference: game->removepacket(); game->endgame();
      this.localGame.terminated = true;
      this.localGame.removePacket();

      // Update game state status to "finished"
      this.controller.setGameStatus("finished");
    }
  }

  /**
   * Update input (called after game logic)
   */
  updateInput(): void {
    if (this.unifiedInput) {
      this.unifiedInput.update();
    }
  }

  /**
   * Cleanup resources
   */
  cleanup(): void {
    if (this.localGame && this.localGame.timeout) {
      this.localGame.stop();
    }
    this.localGame = null;
    this.unifiedInput = null;
    this.controller = null;
    this.gameInitialized = false;
  }
}
